import { memo, useState } from 'react';
import { useAppTranslation } from '@hooks';
import { css } from '@emotion/react';
import { IdManagerRightType } from '@definitions';
import { LoadingOutlined } from '@ant-design/icons';
import { AssignCertification } from '@views/shared/certification';
import { AssignSegmentRight } from '@views/shared/segment';
import { FormUser, FormUserDataType } from '@views/shared/user';
import { Text, Row, ActionModal, colors } from 'common-components';
import { CertificationShortType, SegmentShortType } from '@pages/types';

type CreateManagerModalComponentPropsType = {
  isOpen: boolean
  isLoading: boolean
  onCreateManager(managerForm: FormUserDataType, onSuccess: () => void): void
  onAddSegment: (segmentShort: SegmentShortType, managerRight: IdManagerRightType) => void
  onDeleteSegment: (idSegment: string) => void
  onAddCertification: (
    certificationShort: CertificationShortType,
    managerRight: IdManagerRightType,
  ) => void
  onDeleteCertification: (idCertification: string) => void
  close: () => void
};

const TOTAL_STEPS = 3;

const CreateManagerModalComponent = ({
  onCreateManager,
  onDeleteSegment,
  onAddCertification,
  onDeleteCertification,
  onAddSegment,
  close,
  isOpen,
  isLoading,
}: CreateManagerModalComponentPropsType) => {
  const [managerForm, setManagerForm] = useState<FormUserDataType>(null);

  const { translate } = useAppTranslation();
  const [step, setStep] = useState(1);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  return (
    <>
      <ActionModal
        mask={{ closable: false }}
        width={660}
        key={
          [
            translate('createUser.addManager'),
            translate('common.chooseSegments'),
            translate('common.chooseCertifications'),
          ][step - 1]
        }
        open={isOpen}
        onOk={close}
        customHeader={
          <Row
            css={css`
              flex-direction: column;
              gap: 5px;
            `}
          >
            <Text color={colors.gray400} size="md" style={{ fontWeight: 500 }}>
              {step} {translate('createSegment.outOf')} {TOTAL_STEPS}
            </Text>
            <Text
              css={css`
                font-weight: 500;
              `}
              color={colors.gray900}
              size="lg"
            >
              {step === 1 && translate('createUser.addManager')}
              {step === 2 && translate('common.chooseSegments')}
              {step === 3 && translate('common.chooseCertifications')}
            </Text>
          </Row>
        }
        onConfirm={close}
        // preset={preset}
        title={
          [
            translate('createUser.addManager'),
            translate('common.chooseSegments'),
            translate('common.chooseCertifications'),
          ][step - 1]
        }
        onCancel={() => {
          setStep && setStep(1);
          close();
        }}
        actionButtons={[
          {
            text: translate('managers.cancel'),
            onClick: () => {
              setStep && setStep(1);
              close();
            },
          },
          {
            text: isLoading ? <LoadingOutlined /> : translate('createSegment.next'),
            type: 'primary',
            disabled: !isFormValid,
            onClick: async () => {
              if (setStep) {
                //////// STEP 1 ////////
                if (step === 1) {
                  if (isFormValid && managerForm?.createUser) {
                    onCreateManager(managerForm, () => {
                      setManagerForm(null);
                      setStep(step + 1);
                    });
                  }
                }
                //////// STEP 2 ////////
                if (step === 2) {
                  // TODO add certification
                  setStep(step + 1);
                  return;
                }
                //////// STEP 3 ////////
                if (step === 3) {
                  close();
                  setStep(step + 1);
                  return;
                }
              } else {
                close();
              }
            },
          },
        ]}
      >
        <>
          {step === 1 && (
            <FormUser
              roles={['admin', 'manager']}
              isFormValid={(validation) => setIsFormValid(validation)}
              onChangeForm={setManagerForm}
            />
          )}
          {step === 2 && (
            <AssignSegmentRight onAddSegment={onAddSegment} onDeleteSegment={onDeleteSegment} />
          )}
          {step === 3 && (
            <AssignCertification
              onAddCertification={onAddCertification}
              onDeleteCertification={onDeleteCertification}
            />
          )}
        </>
      </ActionModal>
    </>
  );
};

const CreateManagerModal = memo(
  CreateManagerModalComponent,
  (
    oldProps: CreateManagerModalComponentPropsType,
    newProps: CreateManagerModalComponentPropsType,
  ) => {
    return JSON.stringify(oldProps) === JSON.stringify(newProps);
  },
);

export default CreateManagerModal;
